import { createApi } from '@reduxjs/toolkit/query/react';
import baseQuery from '../../Shared/API/config';

export interface SubscriptionPlan {
  id: number;
  name: string;
  billingType: 'FIXED' | 'PER_STUDENT';
  billingCycle: 'TERMLY' | 'YEARLY';
  basePrice: string;
  perStudentPrice: string;
  features: string[];
}

export interface SubscriptionAgreement {
  id: number;
  version: string;
  content: string;
  signedAt: string | null;
  signedByName: string | null;
  status: 'PENDING' | 'SIGNED';
}

export interface SchoolSubscription {
  id: number;
  plan: SubscriptionPlan;
  status: 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
  startDate: string | null;
  endDate: string | null;
  autoRenew: boolean;
  pendingAgreement: SubscriptionAgreement | null;
}

export interface SchoolInvoice {
  id: number;
  amount: string;
  studentCount: number;
  status: 'UNPAID' | 'PAID' | 'CANCELLED';
  dueDate: string;
  paidAt: string | null;
  receiptNumber: string | null;
}

export const subscriptionInvoicePdfUrl = (invoiceId: number) =>
  `/api/reports/v2/subscription-invoice/print?invoiceId=${invoiceId}`;

export const subscriptionReceiptPdfUrl = (invoiceId: number) =>
  `/api/school/invoices/${invoiceId}/receipt`;

export const licensingApi = createApi({
  reducerPath: 'licensingApi',
  baseQuery,
  tagTypes: ['Subscription', 'Invoices', 'SchoolDetail'],
  endpoints: builder => ({
    getSubscription: builder.query<SchoolSubscription, void>({
      query: () => 'school/subscription',
      providesTags: ['Subscription'],
    }),
    getInvoices: builder.query<SchoolInvoice[], void>({
      query: () => 'school/invoices',
      transformResponse: (response: { results: SchoolInvoice[] }) => response.results,
      providesTags: ['Invoices'],
    }),
    signAgreement: builder.mutation<SubscriptionAgreement, number>({
      query: agreementId => ({
        url: `school/subscription/agreement/${agreementId}/sign`,
        method: 'POST',
      }),
      invalidatesTags: ['Subscription', 'Invoices', 'SchoolDetail'],
    }),
    payInvoice: builder.mutation<
      { checkoutUrl?: string },
      { invoiceId: number; phone_number: string }
    >({
      query: ({ invoiceId, phone_number }) => ({
        url: `school/invoices/${invoiceId}/pay`,
        method: 'POST',
        body: { phone_number },
      }),
      invalidatesTags: ['Invoices', 'SchoolDetail'],
    }),
  }),
});

export const {
  useGetSubscriptionQuery,
  useGetInvoicesQuery,
  useSignAgreementMutation,
  usePayInvoiceMutation,
} = licensingApi;
